// src/lib/learn/read-time.ts
//
// How long a lesson actually takes to read — measured, not guessed.
//
// THIS EXISTS BECAUSE THE GUESS WAS WRONG EVERYWHERE. The generator asked the
// model for `estimatedReadMinutes` and believed the answer; the model said "2"
// for lessons running well over a thousand words, and 79 lessons shipped with a
// badge a student could disprove in a minute. The number is not an opinion —
// it is a word count divided by a reading rate, so it belongs in one pure
// function that the generator, the review checklist and the backfill script all
// call. Anything else lets them drift.
//
// PURE MODULE, ZERO IMPORTS. Both `lesson-generation.ts` (which
// `learning-path.ts` imports for `slugifyTopic`) and `lesson-review.ts` (which
// imports `learning-path.ts`) need this, so it has to sit below all of them or
// the cycle closes.

/** Words a minute. Prose-reading pace; instructional math reads slower, so this
 *  is deliberately a floor rather than a flattering estimate. */
export const WORDS_PER_MINUTE = 200

/** The parts of a lesson a student actually reads top to bottom. */
export type ReadableLessonContent = {
  hook?: string
  explanation?: string
  workedExamples?: Array<{ problem?: string; solution?: string; insight?: string }>
  pitfalls?: Array<{ name?: string; explanation?: string }>
  keyTakeaways?: string[]
}

const words = (s: string | undefined | null): number =>
  s ? s.trim().split(/\s+/).filter(Boolean).length : 0

/**
 * Words on the page: the hook, the explanation, every worked example, every
 * pitfall and every takeaway. Flashcards (a sidebar you flip, not prose) and
 * diagrams are excluded on purpose.
 */
export function lessonWordCount(content: ReadableLessonContent): number {
  let n = words(content.hook) + words(content.explanation)
  for (const ex of content.workedExamples ?? []) {
    n += words(ex.problem) + words(ex.solution) + words(ex.insight)
  }
  for (const p of content.pitfalls ?? []) n += words(p.name) + words(p.explanation)
  for (const t of content.keyTakeaways ?? []) n += words(t)
  return n
}

/** Minutes at WORDS_PER_MINUTE, never less than one. */
export function honestReadMinutes(content: ReadableLessonContent): number {
  return Math.max(1, Math.round(lessonWordCount(content) / WORDS_PER_MINUTE))
}

/**
 * How far a stated time may sit from the measured one and still be honest: a
 * third either way, and never less than a minute (so a 1-minute lesson may say
 * 1 or 2 without being called a liar).
 */
export function readTimeTolerance(honestMinutes: number): number {
  return Math.max(1, Math.ceil(honestMinutes / 3))
}

/** Whether a lesson's stated reading time is defensible. Missing or ≤ 0 is not. */
export function isReadTimeHonest(
  stated: number | null | undefined,
  honestMinutes: number,
): boolean {
  if (typeof stated !== 'number' || !Number.isFinite(stated) || stated <= 0) return false
  return Math.abs(stated - honestMinutes) <= readTimeTolerance(honestMinutes)
}
